//region :: Imports ::
import React, {ReactElement, ReactNode} from 'react';
import MComponent from '../Toolkit/Components/Component';
import {MCancelIconName, MIcon, MOkIconName} from '../Toolkit/Icons';
import {DialogPositionType} from 'primereact/dialog';
import Dialog from '../Toolkit/Components/Controls/Dialog';
import MButton from '../Toolkit/Components/Controls/Button';
import MFormControl from '../Toolkit/Components/Controls/FormControl';
import MTextBox from '../Toolkit/Components/Controls/TextBox';

//endregion

/** */
type Props = {
    onDialogClose: (noteData: string, cancel: boolean) => void;
}

/** */
type State = {
    position?: DialogPositionType;
    visible: boolean;
    pageTitle: string;
    uploading: boolean;
    cancelData: CancelData;
}

export type CancelData = {
    reason: string | undefined;
    plan: string | undefined;
    fault: string | undefined;
    liability: string | undefined;
}

/** */
class CancellationDialog extends MComponent<Props, State> {

    private CancelIcon: MIcon = {name: 'pi pi-trash', color: 'mcolor-info'};

    /** */
    public constructor(props: Props) {
        super(props);

        this.bindMethods();

        this.initializeState();
    }

    /** */
    private bindMethods(): void {
        this.dialog_onHide = this.dialog_onHide.bind(this);

        this.getDialogTemplate = this.getDialogTemplate.bind(this);
        this.getFooterTemplate = this.getFooterTemplate.bind(this);
        this.cancel_Click = this.cancel_Click.bind(this);
        this.ok_Click = this.ok_Click.bind(this);

        this.show = this.show.bind(this);
    }

    /** */
    private initializeState(): void {
        this.state = {
            visible: false,
            pageTitle: 'Job Cancellation Reason',
            uploading: false,
            cancelData: {
                reason: '',
                plan: '',
                fault: '',
                liability: ''
            }
        };
    }

    /** */
    private dialog_onHide(): void {
        this.safeSetState((draft: State) => draft.visible = false);
    }

    /** */
    public show(position?: DialogPositionType) {

        this.safeSetState((draft: State) => {
            draft.position = position;
            draft.visible = true;
            draft.uploading = false;
        });
    }

    public cancel_Click() {
        this.props.onDialogClose('', true);
        this.safeSetState((draft: State) => {
            draft.visible = false;
        });
    }

    private ok_Click() {
        const noteData = 'CANCELATION REASON\n' +
            'What happened: ' + this.state.cancelData.reason + '\n' +
            'What is the plan: ' + this.state.cancelData.plan + '\n' +
            'Who is at fault: ' + this.state.cancelData.fault + '\n' +
            'Liability: ' + this.state.cancelData.liability;
        this.props.onDialogClose(noteData, false);
        this.safeSetState((draft: State) => {
            draft.visible = false;
        });
    }

    private getDialogTemplate(): ReactNode {
        return (
            <div className="formgrid grid">
                <div className="col-12 md:col-12">
                    <MFormControl label={'What happened?'} id={'ReasonField'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MTextBox id="ReasonValue"
                                          disabled={false}
                                          initialValue={this.state.cancelData!.reason}
                                          onChange={e => this.safeSetState((draft: State) => draft.cancelData!.reason = e!.toString())}
                                          maxLength={255}
                                />
                            );
                        }}
                    </MFormControl>
                </div>

                <div className="col-12 md:col-12">
                    <MFormControl label={'What is the plan?'} id={'PlanField'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MTextBox id="PlanValue"
                                          disabled={false}
                                          initialValue={this.state.cancelData!.plan}
                                          onChange={e => this.safeSetState((draft: State) => draft.cancelData!.plan = e!.toString())}
                                          maxLength={255}
                                />
                            );
                        }}
                    </MFormControl>
                </div>

                <div className="col-12 md:col-12">
                    <MFormControl label={'Who is at fault?'} id={'FaultField'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MTextBox id='faultValue'
                                          disabled={false}
                                          initialValue={this.state.cancelData!.fault}
                                          onChange={e => this.safeSetState((draft: State) => draft.cancelData!.fault = e!.toString())}
                                          maxLength={255}
                                />
                            );
                        }}
                    </MFormControl>
                </div>

                <div className="col-12 md:col-12">
                    <MFormControl label={'Liability'} id={'LiabilityField'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MTextBox id="LiabilityField"
                                          disabled={false}
                                          initialValue={this.state.cancelData!.liability}
                                          onChange={e => this.safeSetState((draft: State) => draft.cancelData!.liability = e!.toString())}
                                          maxLength={255}
                                />
                            );
                        }}
                    </MFormControl>
                </div>
            </div>);
    }

    private getFooterTemplate(): ReactNode {
        return (
            <div>
                <MButton label="OK"
                         color="success"
                         icon={MOkIconName}
                         disabled={!this.isUploadAvailable()}
                         onClick={this.ok_Click}/>
                <MButton label="Cancel"
                         icon={MCancelIconName}
                         disabled={false}
                         onClick={this.cancel_Click}/>
            </div>
        );
    }

    private isUploadAvailable() {
        return (this.state.cancelData.reason?.length ?? 0) > 0 &&
            (this.state.cancelData.plan?.length ?? 0) > 0 &&
            (this.state.cancelData.fault?.length ?? 0) > 0 &&
            (this.state.cancelData.liability?.length ?? 0) > 0;
    }

    /** */
    public render(): ReactElement {

        return (
            <Dialog title={this.state.pageTitle}
                    content={this.getDialogTemplate()}
                    icon={this.CancelIcon}
                    footer={this.getFooterTemplate()}
                    position={this.state.position}
                    canClose={false}
                    closeOnEscape={false}
                    visible={this.state.visible}
                    onHide={this.dialog_onHide}/>
        );

    }

}

export default CancellationDialog;
